import { useState, useEffect } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { getPaymentIntent } from "apis";
import { withRedux, priceToDollars, displayError } from "helpers";

import styled, { css } from "react-emotion";
import CheckoutError from "./CheckoutError";
import BillingDetailsFields from "./BillingDetailsFields";

const CardElementContainer = styled("div")`
  height: 40px;
  display: flex;
  align-items: center;
  & .StripeElement {
    width: 100%;
    padding: 15px;
  }
`;

const SubmitButton = styled("button")`
  display: block;
  height: 40px;
  width: 100%;
  font-size: inherit;
  font-weight: bold;
  background-color: ${(props) => (props.disabled ? "#adadad" : props.color)};
  border: none;
  box-shadow: 3px 3px 13px rgba(120, 120, 120, 0.5);
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  color: #fff;
  cursor: pointer;
  margin-top: 50px;
`;

const Title = styled("p")`
  text-align: left;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 1.17em;
`;

const cardErrors = css`
  color: #f17f7f;
`;

// Custom styling can be passed to options when creating an Element.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#F17F7F",
      iconColor: "#F17F7F",
    },
  },
  hidePostalCode: true,
};

const CheckoutForm = (props) => {
  const [error, setError] = useState(null);

  const [isProcessing, setProcessingTo] = useState(false);
  const [cartState, setCartState] = useState(props.rerender);
  const [checkoutError, setCheckoutError] = useState("");
  const [cardFormComplete, setCardFormComplete] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const {
    reqBody,
    user,
    navColor,
    cart,
    selectedCardPayment,
    isAdmin,
    isValidDeliveryAddress,
  } = props;

  const validDeliveryAddress =
    cart.orderDeliveryType === "DELIVERY" ? isValidDeliveryAddress : true;
  // console.log("valid delivery adddress:");
  // console.log(validDeliveryAddress);

  // console.log("card form complete: " + cardFormComplete);
  useEffect(() => {
    // code to run on component mount
  }, [cartState]);
  // Handle real-time validation errors from the card Element.
  const handleChange = (event) => {
    // console.dir(event);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
    setCardFormComplete(event.complete);
  };

  const getClientSecret = async () => {
    // create payment intent on the server
    // client_secret of that payment intent
    let clientSecretObject = await getPaymentIntent(reqBody).then((res) =>
      res.text()
    );

    clientSecretObject = JSON.parse(clientSecretObject);

    if (clientSecretObject.errorCode) {
      setCheckoutError(clientSecretObject.errorMessage);
      displayError(clientSecretObject.errorMessage);
      setProcessingTo(false);
      return;
    }

    return clientSecretObject;
  };

  const invalidCustomerInfo = () => {
    let error = "";
    if (cart.orderCustomerInfo.firstName === "") {
      error = "First name for customer info cannot be blank.";
    } else if (cart.orderCustomerInfo.lastName === "") {
      error = "Last name for customer info cannot be blank.";
    } else if (cart.orderCustomerInfo.email === "") {
      error = "Email for customer info cannot be blank.";
    } else if (cart.orderCustomerInfo.phone === "") {
      error = "Phone number for customer info cannot be blank.";
    }
    if (error != "") {
      setCheckoutError(error);
      displayError(error);
      return true;
    } else {
      return false;
    }
  };

  // Handle selected card submission
  const handleSelectedCardSubmission = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !selectedCardPayment) {
      return;
    }
    if (invalidCustomerInfo()) return;

    setProcessingTo(true);
    // console.log("processing selected card submission");
    try {
      const clientSecretObject = await getClientSecret();
      const clientSecret = clientSecretObject.stripeClientSecret;

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedCardPayment.id,
      });

      if (error) {
        setCheckoutError(error.message);
        displayError(error.message);
        setProcessingTo(false);
        return;
      }

      // success!
      // setProcessingTo(false);
      goToConfirm(clientSecretObject);
    } catch (e) {
      console.error(e);
    }
  };

  // Handle form submission.
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    if (invalidCustomerInfo()) return;

    // console.dir(event);

    const billingDetails = {
      name: event.target.name.value,
      email: event.target.email.value,
      address: {
        city: event.target.city.value,
        line1: event.target.address.value,
        state: event.target.state.value,
        postal_code: event.target.zip.value,
      },
    };

    const cardElement = elements.getElement("card");

    setProcessingTo(true);
    try {
      // need stripe.js
      // need reference to cardElement
      // create a payment method
      const paymentMethodReq = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: billingDetails,
      });

      if (paymentMethodReq.error) {
        setCheckoutError(paymentMethodReq.error.message);
        displayError(paymentMethodReq.error.message);
        setProcessingTo(false);
        return;
      }
      // console.log("got payment method req");
      // console.dir(paymentMethodReq);
      // create payment intent on the server
      // client_secret of that payment intent

      const clientSecretObject = await getClientSecret();
      const clientSecret = clientSecretObject.stripeClientSecret;

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodReq.paymentMethod.id,
      });

      if (error) {
        setCheckoutError(error.message);
        displayError(error.message);
        setProcessingTo(false);
        return;
      }

      // success!
      // setProcessingTo(false);
      goToConfirm(clientSecretObject);
    } catch (e) {
      console.error(e);
      setCheckoutError(e.message);
      displayError(e.message);
    }
  };

  const goToConfirm = (clientSecretObject) => {
    props.actions.setTemplate(props.activeApp.id, null);
    // console.log("going to confirm");
    if (isAdmin) {
      props.history.push({
        pathname: `/${props.activeApp.landingPagePath}/home?isAdmin=true`,
        state: { path: "confirm", clientSecretObject: clientSecretObject },
      });
      return;
    }
    props.history.push({
      pathname: `/${props.activeApp.landingPagePath}/home`,
      state: { path: "confirm", clientSecretObject: clientSecretObject },
    });
  };
  const orderTotal = cart.orderDeliveryType
    ? priceToDollars(cart.clientTotal)
    : priceToDollars(cart.clientCalculatedSubtotal);

  const disabled =
    isProcessing ||
    !stripe ||
    !selectedCardPayment ||
    isAdmin ||
    !validDeliveryAddress;
  // console.log("disabled pay button: " + disabled);
  if (selectedCardPayment)
    return (
      <form onSubmit={handleSelectedCardSubmission}>
        {checkoutError && <CheckoutError>{checkoutError}</CheckoutError>}
        <SubmitButton disabled={disabled} color={navColor}>
          {isProcessing ? "Processing..." : "Pay " + orderTotal}
        </SubmitButton>
      </form>
    );

  return (
    <form onSubmit={handleSubmit}>
      <BillingDetailsFields prefillData={true} user={user} />
      <Title style={{ marginTop: "10px" }}>
        <label>Credit or debit card</label>
      </Title>
      <CardElementContainer>
        <CardElement
          id="card-element"
          options={CARD_ELEMENT_OPTIONS}
          onChange={handleChange}
        />
      </CardElementContainer>

      {checkoutError && <CheckoutError>{checkoutError}</CheckoutError>}
      <SubmitButton
        disabled={
          isProcessing || !stripe || !cardFormComplete || !validDeliveryAddress
        }
        color={navColor}
      >
        {isProcessing ? "Processing..." : "Pay " + orderTotal}
      </SubmitButton>
    </form>
  );
};

export default withRedux(CheckoutForm);

// POST the token ID to your backend.
async function stripeTokenHandler(token) {
  const response = await fetch("/charge", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token: token.id }),
  });

  return response.json();
}
